import SmsIcon from "@mui/icons-material/Sms";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import ContractorPortalApi from "../../../../../infrastructure/api/contractorPortalApi";
import OtpCodeInput from "../../../../components/shared/OtpCodeInput";
import { ContractOtpDialog } from "../../shared/contracts/UniversalContractComponents";
import ContractorPortalPageShell from "../components/ContractorPortalPageShell";
import {
  ContractPdfViewerDialog,
  ContractorContractPageLayout,
} from "../components/contract-ux";
import { CONTRACT_UX } from "../components/contract-ux/contractUxTheme";
import { CONTRACTOR_PORTAL_BASE } from "../contractorPortalNav";
import { downloadPdfBlob } from "../utils/contractPdfActions";
import { resolveContractorContractUx } from "../utils/contractorContractUx";

const DEFAULT_ACCEPTANCE =
  "اینجانب مفاد قرارداد و کلیه پیوست‌ها را مطالعه کرده و با آگاهی کامل می‌پذیرم که ورود کد تأیید به منزله امضای الکترونیکی قرارداد است.";

const ContractorMasterContractSignPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contract, setContract] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [maskedMobile, setMaskedMobile] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    let res = null;
    if (id) {
      res = await ContractorPortalApi.getMasterContractById(id);
    }

    if (!res?.isSuccess || !res?.data?.contractId) {
      const currentBundle = await ContractorPortalApi.getContracts();
      const current = currentBundle?.data?.master;
      if (current?.contractId) {
        res = { isSuccess: true, data: current, message: null };
      }
    }

    if (!res?.isSuccess || !res?.data?.contractId) {
      setError(res?.message || "قراردادی برای نمایش یافت نشد.");
      setContract(null);
    } else {
      setContract(res.data);
      if (res.data?.contractorSignerMaskedMobile) {
        setMaskedMobile(res.data.contractorSignerMaskedMobile);
      }
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const ux = useMemo(
    () =>
      resolveContractorContractUx(contract, {
        contractsPath: `${CONTRACTOR_PORTAL_BASE}/contracts`,
        projectsPath: `${CONTRACTOR_PORTAL_BASE}/projects`,
      }),
    [contract]
  );

  const handleDownload = async () => {
    if (!contract?.contractId) {
      toast.info("فایل قرارداد هنوز در دسترس نیست.");
      return;
    }
    setPdfBusy(true);
    const res = await ContractorPortalApi.downloadMasterContractPdf(
      contract.contractId,
      { preferSigned: true }
    );
    setPdfBusy(false);
    if (!res?.isSuccess || !res.data) {
      toast.error(res?.message || "دانلود قرارداد ناموفق بود.");
      return;
    }
    downloadPdfBlob(
      res.data,
      `contract-${contract.contractNumber || contract.contractId}.pdf`
    );
  };

  const handleView = () => {
    if (!contract?.contractId || !ux.canDownload) {
      toast.info("فایل قرارداد هنوز در دسترس نیست.");
      return;
    }
    setViewerOpen(true);
  };

  const handleActionClick = (action) => {
    if (action?.key === "upload_guarantee" || action?.scrollTo === "guarantee") {
      document
        .getElementById("guarantee-upload-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleSendOtp = async () => {
    if (!accepted) {
      toast.error("لطفاً متن پذیرش قرارداد را تأیید کنید.");
      return;
    }
    setActionLoading(true);
    const res = await ContractorPortalApi.sendMasterContractOtp(contract.contractId);
    setActionLoading(false);
    if (!res?.isSuccess) {
      toast.error(res?.message || "ارسال کد تأیید ناموفق بود.");
      return;
    }
    setMaskedMobile(res.data?.maskedMobile || maskedMobile);
    setOtpOpen(true);
    toast.success(res.data?.message || "کد تأیید ارسال شد.");
    await load();
  };

  const handleVerify = async () => {
    if (!otpCode || otpCode.length < 4) {
      toast.error("کد تأیید را وارد کنید.");
      return;
    }
    setActionLoading(true);
    const res = await ContractorPortalApi.verifyMasterContractSign(
      contract.contractId,
      otpCode
    );
    setActionLoading(false);
    if (!res?.isSuccess) {
      toast.error(res?.message || "تأیید امضا ناموفق بود.");
      return;
    }
    setOtpOpen(false);
    setOtpCode("");
    toast.success("امضای قرارداد با موفقیت ثبت شد.");
    await load();
  };

  const signSlot = ux.showSignFlow ? (
    <Stack spacing={1.5} id="contract-sign-section" data-testid="contract-sign-flow">
      <Alert severity="info" sx={{ py: 0.75 }}>
        {contract?.acceptanceText || DEFAULT_ACCEPTANCE}
      </Alert>
      <Button
        variant={accepted ? "contained" : "outlined"}
        onClick={() => setAccepted((v) => !v)}
        data-testid="acceptance-toggle"
        sx={{
          alignSelf: "flex-start",
          bgcolor: accepted ? CONTRACT_UX.primary : undefined,
          borderColor: CONTRACT_UX.primary,
          color: accepted ? "#fff" : CONTRACT_UX.primary,
          fontWeight: 700,
          "&:hover": {
            bgcolor: accepted ? CONTRACT_UX.primaryDark : "rgba(0,91,143,0.06)",
            borderColor: CONTRACT_UX.primary,
          },
        }}
      >
        {accepted ? "پذیرش تأیید شد ✓" : "متن پذیرش را می‌پذیرم"}
      </Button>
      <Button
        variant="contained"
        size="large"
        startIcon={
          actionLoading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <SmsIcon />
          )
        }
        disabled={!accepted || actionLoading}
        onClick={handleSendOtp}
        data-testid="send-contractor-otp"
        sx={{
          alignSelf: "flex-start",
          bgcolor: CONTRACT_UX.primary,
          fontWeight: 800,
          "&:hover": { bgcolor: CONTRACT_UX.primaryDark },
        }}
      >
        ارسال کد تأیید و امضا
      </Button>
    </Stack>
  ) : null;

  const nextActionOverride = ux.showSignFlow
    ? {
        ...ux.action,
        to: null,
        buttonLabel: null,
      }
    : ux.action?.key === "upload_guarantee"
    ? { ...ux.action, to: null }
    : ux.action;

  return (
    <>
      <ContractorPortalPageShell
        title="قرارداد همکاری"
        compact
        loading={loading || pdfBusy}
        error={null}
      >
        {error && !contract ? (
          <Stack spacing={1.5} dir="rtl">
            <Alert severity="warning" sx={{ py: 1 }}>
              {error}
            </Alert>
            <Button
              component={RouterLink}
              to={`${CONTRACTOR_PORTAL_BASE}/contracts`}
              variant="contained"
              sx={{ alignSelf: "flex-start", bgcolor: CONTRACT_UX.primary }}
            >
              بازگشت به قراردادها
            </Button>
          </Stack>
        ) : null}

        {contract ? (
          <Stack spacing={CONTRACT_UX.sectionGap} data-testid="contractor-master-sign-page">
            <Button
              component={RouterLink}
              to={`${CONTRACTOR_PORTAL_BASE}/contracts`}
              size="small"
              sx={{ alignSelf: "flex-start", color: CONTRACT_UX.primary }}
            >
              بازگشت به قراردادها
            </Button>

            <ContractorContractPageLayout
              ux={ux}
              contract={contract}
              onDownload={handleDownload}
              onView={handleView}
              onActionClick={handleActionClick}
              onGuaranteeUploaded={load}
              nextActionOverride={nextActionOverride}
              actionSlot={signSlot}
            />

            {(ux.phase === "waiting_completion" || ux.phase === "active") && (
              <Button variant="outlined" disabled={actionLoading} onClick={load}>
                تازه‌سازی وضعیت
              </Button>
            )}

            <ContractOtpDialog
              open={otpOpen}
              maskedMobile={maskedMobile}
              otpCode={otpCode}
              onOtpChange={setOtpCode}
              onSubmit={handleVerify}
              onCancel={() => setOtpOpen(false)}
              loading={actionLoading}
              OtpInput={OtpCodeInput}
            />
          </Stack>
        ) : null}
      </ContractorPortalPageShell>

      <ContractPdfViewerDialog
        open={viewerOpen}
        contractId={contract?.contractId}
        contractNumber={contract?.contractNumber}
        onClose={() => setViewerOpen(false)}
      />
    </>
  );
};

export default ContractorMasterContractSignPage;
